// New-task editor. Lives under the merged Suites page (2026-07 nav
// review): /tasks itself redirects to /suites, but the editor keeps its
// own URL for deep links from suite detail + eval pages.
import { useClient } from '@cortex/react-query';
import type { Suite } from '@cortex/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { BackButton } from '../../components/common/BackButton';
import { PageHeader } from '../../components/common/PageHeader';
import {
  TaskEditor,
  buildPayload,
  defaultTaskEditorState,
  validateTask,
  type ITaskEditorState,
} from '../../components/tasks/TaskEditor';
import { routes } from '../../lib/constants';

export const Route = createFileRoute('/tasks/new')({ component: NewTaskPage });

function NewTaskPage() {
  const client = useClient();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [state, setState] = useState<ITaskEditorState>(defaultTaskEditorState);
  const [submitErr, setSubmitErr] = useState<string | null>(null);

  const validation = validateTask(state);

  const create = useMutation({
    mutationFn: async () => {
      setSubmitErr(null);
      const t = await client.http
        .post('v1/tasks', { json: buildPayload(state) })
        .json<{ id: string }>();
      // The eval runner reads membership from the SuiteTask joins, not the
      // task's suiteIds JSONB — sync the joins so the editor's Suite
      // Membership selection actually takes effect (see suite-membership.ts).
      await Promise.all(
        state.suiteIds.map((suiteId) =>
          client.http.put(`v1/suites/${suiteId}/tasks/${t.id}`).json<Suite>(),
        ),
      );
      return t;
    },
    onSuccess: async (t) => {
      await qc.invalidateQueries({ queryKey: ['tasks'] });
      await qc.invalidateQueries({ queryKey: ['suites'] });
      await nav({ to: routes.taskDetail(t.id) });
    },
    onError: (e) => setSubmitErr((e as Error).message),
  });

  return (
    <>
      <PageHeader
        title="New Task"
        actions={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <BackButton to={routes.SUITES} label="Suites" />
            {validation.valid && (
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10,
                  color: 'var(--status-success)',
                  border: '1px solid var(--status-success)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '3px 8px',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                }}
              >
                ✓ Valid
              </span>
            )}
            <button
              className="btn btn--primary btn--sm"
              disabled={!validation.valid || create.isPending}
              onClick={() => create.mutate()}
            >
              {create.isPending ? 'Creating…' : 'Save Task'}
            </button>
          </div>
        }
      />
      {submitErr && (
        <div className="alert alert--danger" style={{ marginBottom: 16 }}>
          {submitErr}
        </div>
      )}
      <TaskEditor state={state} setState={setState} validation={validation} />
    </>
  );
}
